// ============================================================================
// Trainer messaging API — the TRAINER side of the 1:1 thread (/api/clients/*).
// ----------------------------------------------------------------------------
// The mirror of clientApi.messages, but trainer-scoped: the client is named by
// id in the URL (the trainer reads/writes the thread of one of THEIR clients;
// the backend 404s a client this trainer doesn't own, so isolation holds). The
// signed vf_session cookie rides along automatically (credentials: "include").
//
// `body` is plain UTF-8 user text + unicode emoji, rendered ESCAPED by the SPA
// (never HTML/markdown). Images go through the multipart attachment endpoint and
// are fetched back via the owner-gated decrypt-serve URL.
// ============================================================================
import { API_BASE, http } from "./http";
import type {
  Message,
  MessageAttachmentResponse,
  MessageInput,
  MessageListResponse,
  MessageReadResponse,
  MessageUnreadResponse,
  RosterUnreadResponse,
} from "./types";

export const messagesApi = {
  /**
   * Roster unread badges: the grand total + per-client unread (client->trainer)
   * counts across all of THIS trainer's threads. One cheap call feeds every
   * badge (the roster rows + the nav total); kept fresh by the `message`
   * live-sync invalidation, no polling.
   */
  roster: (signal?: AbortSignal) =>
    http.get<RosterUnreadResponse>("/clients/unread", undefined, signal),

  /** One owned client's thread (oldest→newest for display) + unread count. */
  list: (
    clientId: number,
    params: { limit?: number; before_id?: number } = {},
    signal?: AbortSignal,
  ) =>
    http.get<MessageListResponse>(
      `/clients/${clientId}/messages`,
      { limit: params.limit, before_id: params.before_id },
      signal,
    ),

  /** Unread (client→trainer) count for one owned client's thread. */
  unread: (clientId: number, signal?: AbortSignal) =>
    http.get<MessageUnreadResponse>(
      `/clients/${clientId}/messages/unread`,
      undefined,
      signal,
    ),

  /** Post a text message (sender forced to 'trainer' server-side). */
  send: (clientId: number, body: MessageInput) =>
    http.post<Message>(`/clients/${clientId}/messages`, body),

  /** Mark the thread read (stamps read_at on the CLIENT's unread messages). */
  markRead: (clientId: number) =>
    http.post<MessageReadResponse>(`/clients/${clientId}/messages/read`),

  /** Send an image attachment (≤2 MB enforced server-side) + optional caption. */
  sendAttachment: (clientId: number, file: File, body?: string) => {
    const form = new FormData();
    form.append("file", file);
    return http.upload<MessageAttachmentResponse>(
      `/clients/${clientId}/messages/attachment`,
      form,
      body ? { body } : undefined,
    );
  },

  /** Absolute URL of a thread image's decrypt-on-read serve endpoint. */
  attachmentUrl: (clientId: number, photoId: number) =>
    `${API_BASE}/clients/${clientId}/messages/attachment/${photoId}`,
};
